import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import QRCode from "qrcode";
import { Crown, ScanLine, Radar, Lock, Activity, Trophy, Users, User, Flag, Mail, CalendarDays, Globe, Check } from "lucide-react";
import SkeletalHudMock from "../components/landing/SkeletalHudMock";
import { ElitePlayerCard } from "../components/engine/ElitePlayerCard";
import { DEMO_PLAYER, DEMO_ENGINE } from "../components/landing/LabShowcase";
import { Slash, Grit, Ribbon, Heading, Duotone, Plate, CARD_RED, MARK } from "./PrintCard";

const GOLD = "#D4AF37", RED = "#C41E24";
const W = 816, H = 1056; // 8.5in × 11in @ 96dpi
const SITE = "nextosoccer.com";
const QR_URL = "https://nextosoccer.com";
const OFFICIAL_QR = "/marketing/official-qr.png";
const EMAIL = "d.barker@nextosoccer.com";
const BOOKING = "nextosoccer.setmore.com";

const SERVICES = [
  { icon: User, name: "Private 1-on-1 Training", desc: "Individual technical sessions built around your player's weaknesses — every rep filmed for the Engine." },
  { icon: Users, name: "Small-Group Sessions", desc: "3–6 players. Game-speed ball mastery, 1v1 moves and decision-making under pressure." },
  { icon: Flag, name: "HS Pre-Season High Performance Camp", desc: "4-week camp: advanced ball mastery, RSA conditioning, speed & injury prevention + team testing." },
];
const TIERS = [
  { name: "Blueprint", price: "$49", per: "one-time", note: "Vol. 1 Ball Mastery · 12 chapters · Hub" },
  { name: "Tech Track", price: "$29", per: "/mo", note: "Unlimited uploads · Voice feedback" },
  { name: "Elite", price: "$49", per: "/mo", note: "Engine · Skeletal HUD · Elite Card · Locker Room", gold: true },
];
const TECH = [
  [ScanLine, "Next O Technical Engine", "Proprietary video scan rates Balance · Execution · Game Pace"],
  [Activity, "Freeze-Frame Skeletal HUD", "Joint angles, plant foot & hip drive at the exact frame"],
  [Crown, "Animated Elite Card", "FIFA-style card minted on every scan"],
  [Lock, "Private Locker Room", "Elite-only video tips from Coach Barker"],
  [Radar, "Pro-Scout Radar", "Trend lines that show the transformation"],
  [Trophy, "Live Leaderboard", "Rep your club & school across Central Illinois"],
];

// Official Next O QR (uploaded by the coach). Falls back to a generated code only if the asset is missing.
const useQr = () => {
  const [qr, setQr] = useState("");
  useEffect(() => {
    const img = new Image();
    img.onload = () => setQr(OFFICIAL_QR);
    img.onerror = () => QRCode.toDataURL(QR_URL, { margin: 1, width: 600, errorCorrectionLevel: "H", color: { dark: "#0a0a0a", light: "#ffffff" } }).then(setQr);
    img.src = OFFICIAL_QR;
  }, []);
  return qr;
};

const QrBox = ({ qr, size = 132, label = "Scan to start" }) => (
  <div className="flex flex-col items-center gap-2" data-testid="flyer-qr">
    <div className="rounded-xl p-2 bg-white" style={{ boxShadow: `0 0 0 3px ${GOLD}, 0 0 30px ${GOLD}66` }}>
      {qr ? <img src={qr} alt="QR code" style={{ width: size, height: size, display: "block" }} /> : <div style={{ width: size, height: size }} />}
    </div>
    <div className="font-mono text-[10px] font-bold tracking-[0.3em] uppercase" style={{ color: GOLD }}>{label}</div>
    <div className="font-headings text-base uppercase tracking-wider text-white">{SITE}</div>
  </div>
);

const Sheet = ({ children, testid, hero }) => (
  <div data-testid={testid} className="relative overflow-hidden text-white" style={{ width: W, height: H, background: "#070707", fontFamily: "Montserrat, sans-serif" }}>
    <div className="absolute inset-0 elite-carbon-bg" />
    <div className="absolute inset-0 elite-mesh-overlay" />
    <div className="absolute inset-0" style={{ background: `radial-gradient(700px 520px at 78% 18%, ${CARD_RED}55, transparent 65%), radial-gradient(520px 420px at 6% 96%, ${CARD_RED}33, transparent 60%)` }} />
    {hero === "lab" && (
      <>
        <Duotone src="/marketing/hero-male.jpg" pos="50% 6%" style={{ right: -20, top: 30, width: 420, height: 640, opacity: 0.6 }} clip="polygon(22% 0, 100% 0, 100% 100%, 0 100%)" />
        <Slash seed={3} w={520} h={150} style={{ left: -120, top: -50, transform: "rotate(-13deg)" }} />
        <Slash seed={9} w={460} h={130} style={{ right: -110, bottom: 250, transform: "rotate(-16deg)" }} opacity={0.9} />
      </>
    )}
    {hero === "hybrid" && (
      <>
        <Duotone src="/marketing/action-dribble.jpg" pos="58% 20%" style={{ right: -30, top: 0, width: 460, height: 520, opacity: 0.45 }} clip="polygon(26% 0, 100% 0, 100% 100%, 0 100%)" />
        <Slash seed={5} w={520} h={150} style={{ right: -130, top: -60, transform: "rotate(-13deg)" }} />
        <Slash seed={13} w={420} h={120} style={{ left: -110, bottom: 300, transform: "rotate(-15deg)" }} opacity={0.9} />
      </>
    )}
    <div className="absolute inset-x-0 top-0 h-[8px]" style={{ background: `linear-gradient(90deg, ${CARD_RED}, ${GOLD}, ${CARD_RED})` }} />
    <div className="absolute inset-x-0 bottom-0 h-[8px]" style={{ background: `linear-gradient(90deg, ${GOLD}, ${CARD_RED}, ${GOLD})` }} />
    <div className="relative h-full flex flex-col">{children}</div>
    <Grit />
  </div>
);

const Lockup = ({ sub = "Volume 1: Ball Mastery Blueprint · Official Enrollment Open" }) => (
  <div className="flex items-center gap-4">
    <img src="/brand/nexto-academy-horizontal.png" alt="The Next O Technical Academy" className="object-contain" style={{ height: 84, width: "auto", filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.8))" }} />
    <div className="border-l pl-4" style={{ borderColor: `${GOLD}66` }}>
      <div className="font-mono text-[9px] tracking-[0.35em] uppercase" style={{ color: GOLD }}>{sub}</div>
    </div>
  </div>
);

// FLYER 1 — "The Lab" wall poster
const LabFlyer = ({ qr }) => (
  <Sheet testid="flyer-lab" hero="lab">
    <div className="flex items-center justify-between px-12 pt-9">
      <Lockup />
      <div className="text-right">
        <div style={{ fontFamily: MARK, fontSize: 20, lineHeight: 0.95, color: "#e6e6e6", transform: "rotate(-5deg)", textShadow: "0 2px 6px #000" }}>The Next O<br />Standard</div>
        <div className="ml-auto mt-1 h-[3px] w-20" style={{ background: CARD_RED, transform: "rotate(-5deg) skewX(-30deg)" }} />
      </div>
    </div>

    <div className="px-12 mt-7">
      <Heading size={86}>Master the Ball.</Heading>
      <Heading size={86} color={CARD_RED} style={{ marginTop: 6, textShadow: "0 5px 0 rgba(0,0,0,0.85), 0 0 30px rgba(224,20,28,0.75)" }}>Master the Game.</Heading>
      <div className="mt-3 flex items-center gap-4">
        <div className="relative flex items-center shrink-0" style={{ width: 300, height: 26 }}>
          <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, ${CARD_RED}, #b30d13)`, clipPath: "polygon(0 0, 93% 0, 100% 50%, 93% 100%, 0 100%)" }} />
          <span className="relative pl-3 font-mono text-[10px] font-bold tracking-[0.3em] uppercase text-white whitespace-nowrap">Train · Develop · Compete</span>
        </div>
        <p className="text-[13.5px] text-white/75 max-w-[420px] leading-snug">Pro coaching on the grass, backed by the Next O Technical Engine — every rep filmed, frozen, rated.</p>
      </div>
    </div>

    {/* Hero tech visuals */}
    <div className="relative px-12 mt-4" style={{ height: 500 }}>
      <div className="absolute" style={{ left: 48, top: 0, width: 262 }}>
        <SkeletalHudMock compact="header" />
      </div>
      <div className="absolute rounded-2xl border p-3 bg-[#0b0b0b]/95 elite-card-halo" style={{ right: 48, top: 10, width: 458, borderColor: `${GOLD}55` }}>
        <ElitePlayerCard player={DEMO_PLAYER} engine={DEMO_ENGINE} drill="Elastico · Scan 07" api="" animated />
      </div>
      <div className="absolute flex flex-col gap-1.5" style={{ left: 48, bottom: 0, width: 262 }}>
        {[["Freeze-Frame Skeletal HUD", ScanLine], ["Animated Elite Cards", Crown], ["Pro-Scout Radar & Ticker", Radar]].map(([l, I]) => (
          <div key={l} className="flex items-center gap-2 font-mono text-[9px] tracking-[0.18em] uppercase text-white/80 border border-white/12 bg-black/60 rounded-sm px-2.5 py-1.5"><I size={11} style={{ color: GOLD }} /> {l}</div>
        ))}
      </div>
    </div>

    {/* Bottom strip */}
    <div className="mt-auto mx-12 mb-9 rounded-2xl border border-[#D4AF37]/35 bg-black/70 backdrop-blur p-5 grid gap-5" style={{ gridTemplateColumns: "1.15fr 1fr 150px", boxShadow: `0 0 30px ${GOLD}22` }}>
      <div>
        <div className="font-mono text-[9px] font-bold tracking-[0.3em] uppercase mb-2.5" style={{ color: GOLD }}>Technical Academy Tiers</div>
        <div className="space-y-2">
          {TIERS.map((t) => (
            <div key={t.name} className="flex items-center gap-2.5">
              <span className="font-headings text-base uppercase tracking-wide w-[80px]" style={{ color: t.gold ? GOLD : "#fff" }}>{t.name}</span>
              <span className="w-[74px] whitespace-nowrap"><span className="font-cover text-xl leading-none">{t.price}</span> <span className="text-[9px] text-white/45">{t.per}</span></span>
              <span className="text-[9.5px] text-white/55 leading-tight flex-1">{t.note}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="border-l border-white/10 pl-5">
        <div className="font-mono text-[9px] font-bold tracking-[0.3em] uppercase mb-2.5" style={{ color: RED }}>On-Field Training</div>
        <div className="space-y-2.5">
          {SERVICES.map((s) => (
            <div key={s.name} className="flex items-center gap-2 text-[12px] font-semibold text-white/90"><s.icon size={13} style={{ color: RED }} /> {s.name}</div>
          ))}
        </div>
        <div className="text-[10px] text-white/45 mt-3">Book on-field sessions &amp; get the app — scan the code.</div>
      </div>
      <div className="border-l border-white/10 pl-4 flex items-center justify-center"><QrBox qr={qr} size={118} /></div>
    </div>
  </Sheet>
);

// FLYER 2 — "The Hybrid System" handout
const HybridFlyer = ({ qr }) => (
  <Sheet testid="flyer-hybrid" hero="hybrid">
    <div className="flex items-center justify-between px-12 pt-9">
      <Lockup sub="The Hybrid System · On the field · In the app" />
      <div className="text-right">
        <div className="font-headings text-xl uppercase tracking-wide leading-none">Coach Dwain Barker</div>
        <div className="font-mono text-[9px] tracking-[0.3em] uppercase mt-1 text-white/50">Founder · Next O Soccer</div>
      </div>
    </div>

    <div className="px-12 mt-7">
      <div className="flex items-end gap-4">
        <Heading size={64}>One Standard.</Heading>
        <Heading size={64} color={CARD_RED} style={{ textShadow: "0 5px 0 rgba(0,0,0,0.85), 0 0 30px rgba(224,20,28,0.75)" }}>Two Fields.</Heading>
      </div>
      <p className="mt-2 text-[13px] text-white/70 max-w-[640px] leading-snug">Train with Coach Barker on the grass, then keep building every day inside the Next O Elite Training System — the Technical Engine tracks every rep so parents see real, measurable progress.</p>
    </div>

    <div className="px-12 mt-4 grid gap-5" style={{ gridTemplateColumns: "1fr 1fr" }}>
      {/* Left: On-field */}
      <div className="rounded-2xl border p-5 bg-black/60" style={{ borderColor: `${RED}66`, boxShadow: `0 0 26px ${RED}22` }}>
        <div className="flex items-center gap-4 mb-4">
          <img src="/marketing/coach-barker.jpg" alt="Coach Dwain Barker" className="w-[104px] h-[124px] object-cover rounded-xl" style={{ objectPosition: "50% 20%", boxShadow: `0 0 0 3px ${GOLD}, 0 0 24px ${RED}66, 0 10px 30px rgba(0,0,0,0.6)` }} />
          <div>
            <div className="font-mono text-[9px] font-bold tracking-[0.3em] uppercase" style={{ color: RED }}>On-Field Training</div>
            <div className="font-headings text-2xl uppercase tracking-wide leading-tight mt-1">Coached in Person</div>
            <div className="text-[11px] text-white/55 mt-1">Pro-level technical coaching · Central Illinois</div>
          </div>
        </div>
        <div className="space-y-3">
          {SERVICES.map((s) => (
            <div key={s.name} className="flex gap-3 rounded-xl border border-white/10 bg-[#111]/80 p-3">
              <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${RED}26`, color: RED }}><s.icon size={15} /></span>
              <div>
                <div className="text-[13px] font-bold leading-tight">{s.name}</div>
                <div className="text-[10.5px] text-white/60 leading-snug mt-0.5">{s.desc}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3 text-[10px] text-white/50 flex items-center gap-1.5"><CalendarDays size={11} style={{ color: GOLD }} /> Book sessions at {BOOKING}</div>
      </div>

      {/* Right: Digital */}
      <div className="rounded-2xl border p-5 bg-black/60 relative overflow-hidden" style={{ borderColor: `${GOLD}66`, boxShadow: `0 0 26px ${GOLD}22` }}>
        <div className="font-mono text-[9px] font-bold tracking-[0.3em] uppercase" style={{ color: GOLD }}>Digital Elite Training System</div>
        <div className="font-headings text-2xl uppercase tracking-wide leading-tight mt-1">Coached Every Day</div>
        <div className="relative mt-3 flex items-start gap-2" style={{ height: 158 }}>
          <div style={{ width: 104, flexShrink: 0 }}><SkeletalHudMock compact /></div>
          <div className="origin-top-left" style={{ transform: "scale(0.45)", width: 440 }}>
            <div className="rounded-2xl border p-3 bg-[#0b0b0b]/95 elite-card-halo" style={{ borderColor: `${GOLD}55` }}>
              <ElitePlayerCard player={DEMO_PLAYER} engine={DEMO_ENGINE} drill="Elastico · Scan 07" api="" animated />
            </div>
          </div>
        </div>
        <div className="space-y-1.5 mt-2">
          {TECH.map(([I, t, d]) => (
            <div key={t} className="flex items-start gap-2">
              <I size={13} className="mt-0.5 shrink-0" style={{ color: GOLD }} />
              <div className="leading-tight"><span className="text-[12px] font-bold">{t}</span> <span className="text-[10.5px] text-white/55">— {d}</span></div>
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Tiers + How it works + QR */}
    <div className="mt-auto mx-12 mb-8 grid gap-4" style={{ gridTemplateColumns: "1fr 150px" }}>
      <div className="rounded-2xl border border-white/12 bg-black/70 p-4">
        <div className="grid grid-cols-3 gap-3 mb-3">
          {TIERS.map((t) => (
            <div key={t.name} className="rounded-xl border p-3" style={{ borderColor: t.gold ? `${GOLD}88` : "rgba(255,255,255,0.12)", background: t.gold ? `${GOLD}14` : "transparent" }}>
              <div className="flex items-baseline justify-between"><span className="font-headings text-base uppercase tracking-wide" style={{ color: t.gold ? GOLD : "#fff" }}>{t.name}</span><span><span className="font-cover text-xl leading-none">{t.price}</span><span className="text-[9px] text-white/45">{t.per}</span></span></div>
              <div className="text-[9.5px] text-white/55 leading-snug mt-1">{t.note}</div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-3">
          {[["Scan & enroll", "Enroll in the Ball Mastery Blueprint — one enrollment covers up to 5 players in your household."], ["Train & film", "Log reps in the Hub, upload a 15-second clip, let the Engine rate it."], ["Level up", "Book on-field sessions; Elite members unlock the Skeletal HUD & Locker Room."]].map(([t, d], i) => (
            <div key={t} className="flex gap-2">
              <span className="font-cover text-2xl leading-none" style={{ color: RED }}>{i + 1}</span>
              <div><div className="text-[12px] font-bold leading-tight">{t}</div><div className="text-[10px] text-white/55 leading-snug mt-0.5">{d}</div></div>
            </div>
          ))}
        </div>
        <div className="mt-3 pt-2.5 border-t border-white/10 flex flex-wrap items-center gap-x-5 gap-y-1 font-mono text-[9.5px] tracking-[0.12em] text-white/70">
          <span className="flex items-center gap-1.5"><Mail size={11} style={{ color: GOLD }} /> {EMAIL}</span>
          <span className="flex items-center gap-1.5"><CalendarDays size={11} style={{ color: GOLD }} /> {BOOKING}</span>
          <span className="flex items-center gap-1.5"><Globe size={11} style={{ color: GOLD }} /> {SITE}</span>
        </div>
      </div>
      <div className="rounded-2xl border border-[#D4AF37]/40 bg-black/70 flex items-center justify-center p-3"><QrBox qr={qr} size={112} label="Scan to join" /></div>
    </div>
  </Sheet>
);

export default function PrintFlyer() {
  const { id } = useParams();
  const qr = useQr();
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Anton&family=Permanent+Marker&display=swap";
    document.head.appendChild(link);
  }, []);
  useEffect(() => {
    if (!qr) return;
    const t = setTimeout(() => document.fonts.ready.then(() => setReady(true)), 1200);
    return () => clearTimeout(t);
  }, [qr]);
  return (
    <div className="min-h-screen bg-[#050505] flex items-start justify-center" style={{ padding: 0 }}>
      <style>{`@page { size: 8.5in 11in; margin: 0; } html, body { background: #050505; } body { margin: 0; }`}</style>
      {ready && <span data-testid="flyer-ready" className="hidden" />}
      {id === "hybrid" ? <HybridFlyer qr={qr} /> : <LabFlyer qr={qr} />}
    </div>
  );
}
